import { computed, nextTick, onUnmounted, ref, watch, type ComponentPublicInstance } from 'vue'
import { useRoute, useRouter } from 'vue-router'

import { useAndroidBackHandler } from '@/composables/useAndroidBackHandler'
import { useEventListener } from '@/composables/useEventListener'
import { apiService } from '@/services/apiService'
import { logService, LOG_CATEGORIES } from '@/services/logService'
import { requireOnlineModificationAccess } from '@/services/offlineModificationGuardService'
import {
  buildSmartSearchMapSearchAction,
  buildSmartSearchPlaceKey,
  mapFavoritePlaceToSmartSearchFavoriteResult,
  searchMaptilerPlaces,
  type SmartSearchAppResult,
  type SmartSearchLocationEventDetail,
  type SmartSearchPlaceResult,
} from '@/services/smartSearchService'
import { useAuthStore } from '@/stores/authStore'
import { useLibraryStore } from '@/stores/libraryStore'
import { useMapStore } from '@/stores/mapStore'
import { useUiStore } from '@/stores/uiStore'
import type { FavoritePlace } from '@/types/favoritePlace'

export type SmartSearchDisplayedResult = SmartSearchAppResult | SmartSearchPlaceResult
type SmartSearchFavoriteAppResult = Extract<SmartSearchAppResult, { kind: 'favorite-place' }>
type SmartSearchLocationResult = SmartSearchPlaceResult | SmartSearchFavoriteAppResult

const LIVE_MAP_SEARCH_MIN_LENGTH = 4

export function useSmartSearch() {
  const uiStore = useUiStore()
  const authStore = useAuthStore()
  const libraryStore = useLibraryStore()
  const mapStore = useMapStore()
  const router = useRouter()
  const route = useRoute()

  const query = ref('')
  const favoritePlaces = ref<FavoritePlace[]>([])
  const appResults = ref<SmartSearchAppResult[]>([])
  const placeResults = ref<SmartSearchPlaceResult[]>([])
  const localError = ref<string | null>(null)
  const placeError = ref<string | null>(null)
  const isLocalLoading = ref(false)
  const isPlaceLoading = ref(false)
  const isFavoritesLoading = ref(false)
  const activeIndex = ref(0)
  const isShowingPlaceResults = ref(false)
  const inputRef = ref<HTMLInputElement | null>(null)
  const resultButtonRefs = ref<Array<HTMLButtonElement | null>>([])
  const pendingFavoriteKeys = ref<string[]>([])

  let favoritePlacesRequestId = 0
  let appSearchTimer: ReturnType<typeof setTimeout> | null = null
  let appSearchRequestId = 0
  let placeSearchTimer: ReturnType<typeof setTimeout> | null = null
  let placeSearchRequestId = 0
  let placeSearchAbortController: AbortController | null = null

  const isOpen = computed(() => uiStore.isSmartSearchOpen)
  const hasLiveMapQuery = computed(() => query.value.trim().length >= LIVE_MAP_SEARCH_MIN_LENGTH)
  const queryResults = computed<SmartSearchAppResult[]>(() => {
    const trimmedQuery = query.value.trim()
    if (!trimmedQuery) return appResults.value
    return hasLiveMapQuery.value
      ? appResults.value
      : [buildSmartSearchMapSearchAction(trimmedQuery), ...appResults.value]
  })
  const hasDefaultFavoriteResults = computed(
    () =>
      !query.value.trim() && appResults.value.some((result) => result.kind === 'favorite-place'),
  )
  const favoritePlacesByKey = computed(() => {
    const entries = favoritePlaces.value.map((favoritePlace): [string, FavoritePlace] => [
      buildSmartSearchPlaceKey(favoritePlace),
      favoritePlace,
    ])
    return new Map<string, FavoritePlace>(entries)
  })
  const inlinePlaceResults = computed(() =>
    !isShowingPlaceResults.value && hasLiveMapQuery.value
      ? placeResults.value.filter(
          (placeResult) => !favoritePlacesByKey.value.has(buildSmartSearchPlaceKey(placeResult)),
        )
      : [],
  )
  const displayedResults = computed<SmartSearchDisplayedResult[]>(() =>
    isShowingPlaceResults.value
      ? placeResults.value
      : query.value.trim()
        ? [...queryResults.value, ...inlinePlaceResults.value]
        : appResults.value,
  )
  const activeResult = computed(() => displayedResults.value[activeIndex.value] ?? null)

  // Presentation helpers shared by every search surface (desktop pill + mobile sheet) so the
  // "show the results area?" decision is computed once.
  const hasSearchQuery = computed(() => query.value.trim().length > 0)
  const shouldShowResultsDropdown = computed(() => {
    if (localError.value || (isShowingPlaceResults.value && placeError.value)) return true
    if (
      (hasSearchQuery.value && isLocalLoading.value && displayedResults.value.length === 0) ||
      (isShowingPlaceResults.value && isPlaceLoading.value) ||
      (!isShowingPlaceResults.value &&
        hasLiveMapQuery.value &&
        isPlaceLoading.value &&
        displayedResults.value.length === 0)
    ) {
      return true
    }
    if (displayedResults.value.length > 0) return true
    return hasSearchQuery.value
  })

  const focusInput = () => {
    nextTick(() => {
      inputRef.value?.focus()
      inputRef.value?.select()
    })
  }

  const closeSearch = () => {
    uiStore.closeSmartSearch()
  }

  // Android hardware back closes map search before navigating the route.
  useAndroidBackHandler(() => uiStore.isSmartSearchOpen, closeSearch)

  const openSearch = () => {
    if (uiStore.isSmartSearchOpen) {
      focusInput()
      return
    }
    uiStore.openSmartSearch()
    focusInput()
  }

  const toggleSearch = () => {
    if (uiStore.isSmartSearchOpen) {
      closeSearch()
      return
    }
    openSearch()
  }

  const isLocationResult = (
    result: SmartSearchDisplayedResult | null,
  ): result is SmartSearchLocationResult =>
    result?.kind === 'place' || result?.kind === 'favorite-place'

  const getFavoriteKey = (result: SmartSearchLocationResult) => buildSmartSearchPlaceKey(result)

  const getFavoritePlaceForResult = (result: SmartSearchLocationResult) =>
    favoritePlacesByKey.value.get(getFavoriteKey(result)) ?? null

  const upsertFavoritePlaces = (favoritePlace: FavoritePlace) => {
    favoritePlaces.value = [
      favoritePlace,
      ...favoritePlaces.value.filter((currentFavorite) => currentFavorite.id !== favoritePlace.id),
    ]
  }

  const upsertAppFavoriteResult = (favoritePlace: FavoritePlace) => {
    const nextFavoriteResult = mapFavoritePlaceToSmartSearchFavoriteResult(favoritePlace)
    appResults.value = [
      nextFavoriteResult,
      ...appResults.value.filter((result) => {
        if (result.kind !== 'favorite-place') return true
        return (
          result.favoritePlaceId !== favoritePlace.id &&
          buildSmartSearchPlaceKey(result) !== buildSmartSearchPlaceKey(nextFavoriteResult)
        )
      }),
    ]
  }

  const removeAppFavoriteResult = (favoritePlace: FavoritePlace) => {
    appResults.value = appResults.value.filter((result) => {
      if (result.kind !== 'favorite-place') return true
      return (
        result.favoritePlaceId !== favoritePlace.id &&
        buildSmartSearchPlaceKey(result) !== buildSmartSearchPlaceKey(favoritePlace)
      )
    })
  }

  const isFavoriteMutationPending = (result: SmartSearchDisplayedResult | null) =>
    isLocationResult(result) && pendingFavoriteKeys.value.includes(getFavoriteKey(result))

  const isResultFavorited = (result: SmartSearchDisplayedResult | null) =>
    isLocationResult(result) && getFavoritePlaceForResult(result) !== null

  const favoriteActionLabel = (result: SmartSearchDisplayedResult | null) => {
    if (!isLocationResult(result)) return 'Save place'
    return isResultFavorited(result) ? 'Remove favorite place' : 'Save favorite place'
  }

  const trackFavoriteMutation = (key: string, pending: boolean) => {
    pendingFavoriteKeys.value = pending
      ? [...pendingFavoriteKeys.value, key]
      : pendingFavoriteKeys.value.filter((pendingKey) => pendingKey !== key)
  }

  const setResultButtonRef = (element: Element | ComponentPublicInstance | null, index: number) => {
    resultButtonRefs.value[index] = element instanceof HTMLButtonElement ? element : null
  }

  const scrollActiveResultIntoView = () => {
    nextTick(() => {
      if (!uiStore.isSmartSearchOpen || displayedResults.value.length === 0) return
      const activeButton = resultButtonRefs.value[activeIndex.value]
      if (
        !(activeButton instanceof HTMLElement) ||
        typeof activeButton.scrollIntoView !== 'function'
      ) {
        return
      }
      activeButton.scrollIntoView({ block: 'nearest' })
    })
  }

  const loadFavoritePlaces = async () => {
    if (!authStore.isAuthenticated) {
      favoritePlaces.value = []
      isFavoritesLoading.value = false
      return
    }

    const requestId = ++favoritePlacesRequestId
    isFavoritesLoading.value = true

    try {
      const results = await apiService.getFavoritePlaces({ suppressToast: true })
      if (requestId !== favoritePlacesRequestId) return
      favoritePlaces.value = results
    } catch (error) {
      if (requestId !== favoritePlacesRequestId) return
      localError.value = error instanceof Error ? error.message : 'Failed to load favorite places'
      logService.error(LOG_CATEGORIES.API, 'Failed to load smart-search favorite places', { error })
    } finally {
      if (requestId === favoritePlacesRequestId) {
        isFavoritesLoading.value = false
      }
    }
  }

  const searchAppResults = async (searchQuery: string) => {
    const requestId = ++appSearchRequestId
    const trimmedQuery = searchQuery.trim()
    if (!authStore.isAuthenticated) {
      appResults.value = []
      localError.value = null
      isLocalLoading.value = false
      return
    }

    isLocalLoading.value = true
    localError.value = null

    try {
      const results = await apiService.getSmartSearchResults(
        {
          query: trimmedQuery || undefined,
          limit: 12,
        },
        { suppressToast: true },
      )
      if (requestId !== appSearchRequestId) return
      appResults.value = results
    } catch (error) {
      if (requestId !== appSearchRequestId) return
      appResults.value = []
      localError.value =
        error instanceof Error ? error.message : 'Failed to load Smart Search results'
      logService.error(LOG_CATEGORIES.API, 'Failed to load Smart Search backend results', { error })
    } finally {
      if (requestId === appSearchRequestId) {
        isLocalLoading.value = false
      }
    }
  }

  const scheduleAppSearch = (searchQuery: string) => {
    if (appSearchTimer) {
      clearTimeout(appSearchTimer)
      appSearchTimer = null
    }

    appSearchTimer = setTimeout(() => {
      void searchAppResults(searchQuery)
    }, 220)
  }

  const searchPlaces = async (
    searchQuery: string,
    options: { showPlaceResults?: boolean; allowShortQuery?: boolean } = {},
  ) => {
    const trimmedQuery = searchQuery.trim()
    if (!trimmedQuery) return
    if (!options.allowShortQuery && trimmedQuery.length < LIVE_MAP_SEARCH_MIN_LENGTH) {
      placeResults.value = []
      placeError.value = null
      isPlaceLoading.value = false
      return
    }

    const requestId = ++placeSearchRequestId
    placeSearchAbortController?.abort()
    placeSearchAbortController = new AbortController()
    isPlaceLoading.value = true
    placeError.value = null

    try {
      const center = mapStore.mapInstance?.getCenter?.()
      const results = await searchMaptilerPlaces(trimmedQuery, {
        signal: placeSearchAbortController.signal,
        limit: 8,
        viewportBias: center ? { proximity: { lon: center.lng, lat: center.lat } } : undefined,
      })
      if (requestId !== placeSearchRequestId) return
      placeResults.value = results
      if (options.showPlaceResults) {
        isShowingPlaceResults.value = true
        activeIndex.value = 0
      }
    } catch (error) {
      if (error instanceof DOMException && error.name === 'AbortError') {
        return
      }
      if (requestId !== placeSearchRequestId) return
      placeError.value =
        error instanceof Error ? error.message : 'Failed to load map search results'
      logService.error(LOG_CATEGORIES.API, 'Failed to load smart-search map results', { error })
    } finally {
      if (requestId === placeSearchRequestId) {
        isPlaceLoading.value = false
      }
    }
  }

  const schedulePlaceSearch = (searchQuery: string) => {
    if (placeSearchTimer) {
      clearTimeout(placeSearchTimer)
      placeSearchTimer = null
    }

    const trimmedQuery = searchQuery.trim()
    if (!trimmedQuery || trimmedQuery.length < LIVE_MAP_SEARCH_MIN_LENGTH) {
      placeSearchAbortController?.abort()
      placeSearchAbortController = null
      placeResults.value = []
      placeError.value = null
      isPlaceLoading.value = false
      return
    }

    placeResults.value = []
    placeError.value = null
    isPlaceLoading.value = true
    placeSearchTimer = setTimeout(() => {
      void searchPlaces(trimmedQuery)
    }, 260)
  }

  const runPlaceSearch = async () => {
    await searchPlaces(query.value, { showPlaceResults: true, allowShortQuery: true })
  }

  const openLocationOnMap = (result: SmartSearchLocationResult) => {
    const detail: SmartSearchLocationEventDetail = {
      flyTo: {
        lat: result.lat,
        lon: result.lon,
        zoom: 15,
      },
      name: result.title,
      description: result.subtitle || undefined,
      type: result.placeType ?? undefined,
    }

    window.dispatchEvent(
      new CustomEvent<SmartSearchLocationEventDetail>('smart-search-open-location', { detail }),
    )
  }

  const toggleFavoritePlace = async (result: SmartSearchDisplayedResult | null) => {
    if (!authStore.isAuthenticated || !isLocationResult(result)) return
    if (!(await requireOnlineModificationAccess('favorite places'))) return

    const favoriteKey = getFavoriteKey(result)
    if (pendingFavoriteKeys.value.includes(favoriteKey)) return

    trackFavoriteMutation(favoriteKey, true)

    try {
      const currentFavorite = getFavoritePlaceForResult(result)
      if (currentFavorite) {
        await apiService.deleteFavoritePlace(currentFavorite.id, { suppressToast: true })
        favoritePlaces.value = favoritePlaces.value.filter(
          (favoritePlace) => favoritePlace.id !== currentFavorite.id,
        )
        removeAppFavoriteResult(currentFavorite)
        return
      }

      const createdFavorite = await apiService.createFavoritePlace(
        {
          title: result.title,
          subtitle: result.subtitle || null,
          lat: result.lat,
          lon: result.lon,
          placeType: result.placeType,
          source: 'maptiler',
          sourceId: 'sourceId' in result ? result.sourceId : null,
        },
        { suppressToast: true },
      )
      upsertFavoritePlaces(createdFavorite)
      upsertAppFavoriteResult(createdFavorite)
      if (!isShowingPlaceResults.value && query.value.trim()) {
        activeIndex.value = hasLiveMapQuery.value ? 0 : 1
      }
    } catch (error) {
      localError.value = error instanceof Error ? error.message : 'Failed to update favorite place'
      logService.error(LOG_CATEGORIES.API, 'Failed to toggle smart-search favorite place', {
        error,
      })
    } finally {
      trackFavoriteMutation(favoriteKey, false)
    }
  }

  const handleFavoriteButtonClick = (index: number, result: SmartSearchDisplayedResult) => {
    activeIndex.value = index
    void toggleFavoritePlace(result)
  }

  const activateResult = async (result: SmartSearchDisplayedResult | null) => {
    if (!result) return

    if (result.kind === 'map-search') {
      await runPlaceSearch()
      return
    }

    if (result.kind === 'place' || result.kind === 'favorite-place') {
      openLocationOnMap(result)
      closeSearch()
      return
    }

    if (result.kind === 'workspace') {
      await router.push({ name: 'workspace-detail', params: { workspaceId: result.workspaceId } })
      closeSearch()
      return
    }

    if (result.kind === 'workspace-tour') {
      await router.push({
        name: 'tour-detail',
        params: { workspaceId: result.workspaceId, tourId: result.tourId },
      })
      closeSearch()
      return
    }

    if (result.kind === 'workspace-track') {
      uiStore.queueSmartSearchTrackSelection({
        workspaceId: result.workspaceId,
        tourId: result.tourId,
        trackClientId: result.trackClientId,
      })
      await router.push({
        name: 'tour-detail',
        params: { workspaceId: result.workspaceId, tourId: result.tourId },
      })
      closeSearch()
      return
    }

    window.dispatchEvent(new CustomEvent('open-all-activities'))
    await nextTick()
    libraryStore.setSelectedTourIds([result.tour.clientId])
    closeSearch()
  }

  const moveSelection = (direction: 1 | -1) => {
    const resultCount = displayedResults.value.length
    if (resultCount === 0) return
    activeIndex.value = (activeIndex.value + direction + resultCount) % resultCount
  }

  const showLocalResults = () => {
    isShowingPlaceResults.value = false
    activeIndex.value = 0
    focusInput()
  }

  const handleInputKeydown = async (event: KeyboardEvent) => {
    if (event.key === 'ArrowDown') {
      event.preventDefault()
      moveSelection(1)
      return
    }

    if (event.key === 'ArrowUp') {
      event.preventDefault()
      moveSelection(-1)
      return
    }

    if (event.key === 'Enter') {
      event.preventDefault()
      await activateResult(activeResult.value)
    }
  }

  const handleGlobalShortcut = (event: KeyboardEvent) => {
    const key = typeof event.key === 'string' ? event.key.toLowerCase() : ''

    if (
      uiStore.isSmartSearchOpen &&
      !event.altKey &&
      !event.ctrlKey &&
      !event.metaKey &&
      !event.shiftKey
    ) {
      if (key === 'escape') {
        event.preventDefault()
        closeSearch()
        return
      }
    }

    const isFavoriteShortcut =
      uiStore.isSmartSearchOpen &&
      !event.repeat &&
      !event.altKey &&
      !event.shiftKey &&
      key === 'd' &&
      (event.metaKey || event.ctrlKey)

    if (isFavoriteShortcut) {
      event.preventDefault()
      void toggleFavoritePlace(activeResult.value)
      return
    }

    const isCommandPaletteShortcut =
      !event.repeat &&
      !event.altKey &&
      !event.shiftKey &&
      key === 'k' &&
      (event.metaKey || event.ctrlKey)

    if (!isCommandPaletteShortcut) return
    if (event.defaultPrevented) return
    event.preventDefault()

    if (uiStore.isSmartSearchOpen) {
      closeSearch()
      return
    }

    openSearch()
  }

  const resetActiveRequests = () => {
    if (appSearchTimer) {
      clearTimeout(appSearchTimer)
      appSearchTimer = null
    }
    if (placeSearchTimer) {
      clearTimeout(placeSearchTimer)
      placeSearchTimer = null
    }
    placeSearchAbortController?.abort()
    placeSearchAbortController = null
    isLocalLoading.value = false
    isPlaceLoading.value = false
    isFavoritesLoading.value = false
  }

  watch(
    () => uiStore.isSmartSearchOpen,
    (isOpen) => {
      if (isOpen) {
        focusInput()
        void Promise.all([loadFavoritePlaces(), searchAppResults(query.value)])
        return
      }

      resetActiveRequests()
    },
  )

  watch(query, (nextQuery) => {
    activeIndex.value = 0
    isShowingPlaceResults.value = false
    scheduleAppSearch(nextQuery)
    schedulePlaceSearch(nextQuery)
  })

  watch(displayedResults, (results) => {
    resultButtonRefs.value = resultButtonRefs.value.slice(0, results.length)
    if (results.length === 0) {
      activeIndex.value = 0
      return
    }

    if (activeIndex.value >= results.length) {
      activeIndex.value = results.length - 1
      return
    }

    scrollActiveResultIntoView()
  })

  watch(activeIndex, () => {
    scrollActiveResultIntoView()
  })

  watch(
    () => route.fullPath,
    () => {
      if (uiStore.isSmartSearchOpen) {
        closeSearch()
      }
    },
  )

  useEventListener(document, 'keydown', handleGlobalShortcut)

  onUnmounted(() => {
    resetActiveRequests()
  })

  return {
    activeIndex,
    appResults,
    displayedResults,
    favoriteActionLabel,
    handleFavoriteButtonClick,
    handleInputKeydown,
    hasDefaultFavoriteResults,
    hasLiveMapQuery,
    inlinePlaceResults,
    inputRef,
    hasSearchQuery,
    shouldShowResultsDropdown,
    isFavoriteMutationPending,
    isFavoritesLoading,
    isLocalLoading,
    isLocationResult,
    isOpen,
    isPlaceLoading,
    isResultFavorited,
    isShowingPlaceResults,
    localError,
    openSearch,
    placeError,
    query,
    setResultButtonRef,
    showLocalResults,
    toggleSearch,
    closeSearch,
    activateResult,
  }
}
